import {
  AD_ACCOUNT_PLATFORMS_LABELS,
  BORDER_RADIUS,
  FONT_FAMILY,
  LINKEDIN,
  ADS_CAMPAIGN_OBJECTIVE_LEADS_OPTION,
  ADS_CAMPAIGN_TYPE_BUDGET_DAILY_OPTION,
  ADS_CAMPAIGN_OBJECTIVE_VIDEO_VIEWS_OPTION,
  LINKEDIN_AD_FORMAT_DOCUMENT,
  MIN_WIDTH_SCREEN_TO_SHOW_CHAT,
  linkedinCurrenciesWithDecimals,
} from "../../../../utils/constants";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { ChipAIGeneratedAnySection } from "../../../Chip/ChipAIGenerated";
import {
  AI_GENERATED_DEMOGRAPHICS_BASIC_SECTIONS,
  AI_GENERATED_LINKEDIN_AUDIENCE_ATTRIBUTE_SECTIONS,
} from "../../../../utils/aiGenerated";
import {
  FIELD_REQUIRED,
  ADS_CAMPAIGN_AD_GROUP_BUDGET_INFORMATION_SUB_TITLE,
  TYPE_BUDGET_PLACEHOLDER,
  LIFETIME_BUDGET_PLACEHOLDER,
  DAILY_BUDGET_PLACEHOLDER,
  START_DATE_PLACEHOLDER,
  END_DATE_PLACEHOLDER,
  ALERT_CHANGE_AD_ACCOUNT_GENERAL_TITLE,
  ALERT_CHANGE_AD_ACCOUNT_LINKEDIN_DESCRIPTION,
  BUTTON_CONTINUE,
  BUTTON_CANCEL,
  ADS_CAMPAIGN_AD_GROUP_BUDGET_INFORMATION_GENERAL,
  LOCATIONS_PLACEHOLDER,
  LANGUAGES_PLACEHOLDER,
  OBJECTIVE_PLACEHOLDER,
  AD_FORMAT_PLACEHOLDER,
  AGE_RANGE_PLACEHOLDER,
  GENDERS_PLACEHOLDER,
  MALES,
  FEMALES,
  ADS_CAMPAIGN_SEGMENTATION_SUB_TITLE,
  DEMOGRAPHICS_BASIC_PLACEHOLDER,
  INTERESTS_PLACEHOLDER,
  INDUSTRIES_PLACEHOLDER,
  JOB_FUNCTIONS_PLACEHOLDER,
  SENIORITIES_PLACEHOLDER,
  JOB_TITLES_PLACEHOLDER,
  COMPANY_SIZES_PLACEHOLDER,
  COMPANIES_PLACEHOLDER,
  DEGREES_PLACEHOLDER,
  FIELDS_OF_STUDY_PLACEHOLDER,
  SCHOOLS_PLACEHOLDER,
  SKILLS_PLACEHOLDER,
  MEMBER_BEHAVIORS_PLACEHOLDER,
  DEGREES_SEARCH_PLACEHOLDER,
  FIELDS_OF_STUDY_SEARCH_PLACEHOLDER,
  INTERESTS_SEARCH_PLACEHOLDER,
  SCHOOLS_SEARCH_PLACEHOLDER,
  SKILLS_SEARCH_PLACEHOLDER,
  COMPANIES_SEARCH_PLACEHOLDER,
  INDUSTRIES_SEARCH_PLACEHOLDER,
  COMPANY_SIZES_SEARCH_PLACEHOLDER,
  JOB_FUNCTIONS_SEARCH_PLACEHOLDER,
  JOB_TITLES_SEARCH_PLACEHOLDER,
  SENIORITIES_SEARCH_PLACEHOLDER,
  MEMBER_BEHAVIORS_SEARCH_PLACEHOLDER,
  AUDIENCE_ATTRIBUTES_PLACEHOLDER,
  CONVERSION_ACTIONS_PLACEHOLDER,
  TARGET_CPA_PLACEHOLDER,
} from "../../../../i18n/keysTranslations";
import Select from "../../../Form/Select";
import SelectBusinessAccount from "../../../Meta/SelectBusinessAccount";
import { addDays } from "date-fns";
import { useCallback, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import useFocusErrorForm from "../../../../hooks/useFocusErrorForm";
import { useTranslationApp } from "../../../../lib/i18next";
import SearchSegmentationsField from "../../../Form/SearchSegmentationsField";
import { useDispatchApp } from "../../../../lib/redux";
import ReasoningContainer from "../../../../pages/NewAdsCampaign/components/Common/ReasoningContainer";
import NumberDecimalField from "../../../Form/NumberDecimalField";
import NumberField from "../../../Form/NumberField";
import { getCurrencyAdAccountPlatform } from "../../../../actions/getters";
import DateField from "../../../Form/DateField";
import startOfDay from "date-fns/startOfDay";
import { ConfirmAlert } from "../../../Alerts/Alerts";
import {
  BOX_SHADOW_FIELDS,
  INPUT_COLOR_BORDER,
} from "../../../../utils/colors";
import { Box, Slider, Divider, useMediaQuery } from "@mui/material";
import { People, Person, LocationOnOutlined } from "@mui/icons-material";
import CardContainer from "../../../Card/CardContainer";
import SelectLanguagesLinkedinForm from "./SelectLanguagesLinkedinForm";
import AudienceEstimatesPreviewLinkedin from "./AudienceEstimatesPreviewLinkedin";
import TextField from "../../../Form/TextField";
import CheckBoxes from "../../../Form/CheckBoxes";
import SelectConversionActionsLinkedin from "../../../LinkedIn/SelectConversionActionsLinkedin";
import CustomAudiencesContainerLinkedin from "../Common/CustomAudiencesContainerLinkedin";
import {
  optionsAdsCampaignTypeBudgets,
  optionsAdsCampaignObjectivesLinkedin,
  optionsAdsCampaignAdFormatLinkedin,
  marksAge,
} from "../../../../utils/options";
import {
  optionsStaffCountRangesLinkedin,
  optionsJobFunctionsLinkedin,
  optionsSenioritiesLinkedin,
  optionsMemberBehaviorsLinkedin,
} from "../../../../utils/linkedinTargetingOptions";
import { textNumberCompactFormat } from "../../../../utils/numbers";

const platform = LINKEDIN;
const grid = {
  xs: 12,
  lg: 6,
};

const CampaignLinkedinForm = () => {
  const {
    control,
    formState,
    watch,
    setValue,
    getValues,
    isGeneralDisabledFields,
    getIsCampaignPlatformCreated,
    isChatVisible,
  } = useFormContext();
  useFocusErrorForm(formState);
  const errors = formState.errors;

  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();

  // Responsive breakpoints
  const isMediumScreen = useMediaQuery(
    "(min-width: 1200px) and (max-width: 1399px)"
  );
  const isLargeScreen = useMediaQuery("(min-width: 1400px)");
  const isLargeScreenForChat = useMediaQuery(
    () => `(min-width: ${MIN_WIDTH_SCREEN_TO_SHOW_CHAT}px)`
  );
  const showSlideChat = isChatVisible && isLargeScreenForChat;

  // Determine layout based on screen size and chat state
  // Two-column layout: medium screens (1200-1399px) or large screens without chat (>1400px)
  const isTwoColumnLayout = isMediumScreen || (isLargeScreen && !showSlideChat);

  // In two-column layout: form first, preview second
  // In single-column layout: preview on top, form below
  const containerFlexDirection = isTwoColumnLayout ? "row" : "column";
  const formOrder = isTwoColumnLayout ? 1 : 2;
  const previewOrder = isTwoColumnLayout ? 2 : 1;

  // Form width: in two-column layout needs space for preview (400px + gap)
  const formMaxWidth = isTwoColumnLayout ? "calc(100% - 420px)" : "100%";

  // Preview width: fixed width in two-column layout, full width in single-column layout
  const previewWidth = isTwoColumnLayout ? "400px" : "100%";

  // Preview position: sticky in two-column layout to keep it visible when scrolling
  const previewPosition = isTwoColumnLayout ? "sticky" : "static";
  const previewTop = isTwoColumnLayout ? 0 : "auto";

  const basicPath = `platformsData.${platform}`;
  const platformData = watch(basicPath);
  const isIncompatible = platformData?.isIncompatible;
  const typeBudget = platformData?.typeBudget;
  const startDate = platformData?.startDate;
  const endDate = platformData?.endDate;
  const language = watch("language");
  const genders = watch(`${basicPath}.genders`) || {};
  const objective = watch(`${basicPath}.objective`);
  const adFormatOptions = useMemo(() => {
    if (objective === ADS_CAMPAIGN_OBJECTIVE_VIDEO_VIEWS_OPTION) {
      return optionsAdsCampaignAdFormatLinkedin.filter(
        (option) => option.value !== LINKEDIN_AD_FORMAT_DOCUMENT
      );
    }
    return optionsAdsCampaignAdFormatLinkedin;
  }, [objective]);

  const isCampaignPlatformCreated = getIsCampaignPlatformCreated(platform);

  const isDisabledFields = isIncompatible || isGeneralDisabledFields;

  const currency = dispatch(
    getCurrencyAdAccountPlatform({
      platform,
      integrationID: platformData?.integrationID,
      adAccountID: platformData?.adAccountID,
    })
  );

  const validationChangeAdAccount = async () => {
    const result = await ConfirmAlert({
      title: t(ALERT_CHANGE_AD_ACCOUNT_GENERAL_TITLE),
      html: (
        <Typography
          variant="body1"
          sx={{
            fontFamily: FONT_FAMILY,
            "& *": {
              p: "revert",
              m: "revert",
              listStyle: "revert",
              textDecoration: "revert",
            },
          }}
          dangerouslySetInnerHTML={{
            __html: t(ALERT_CHANGE_AD_ACCOUNT_LINKEDIN_DESCRIPTION),
          }}
        />
      ),
      confirmButtonText: t(BUTTON_CONTINUE),
      cancelButtonText: t(BUTTON_CANCEL),
    });

    if (!result.isConfirmed) return false;

    return true;
  };

  const onChangeAdAccount = (value) => {
    setValue(`${basicPath}.integrationID`, value?.integrationID, {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue(`${basicPath}.budget`, "");
    const assetsGroups = getValues("assetsGroups");
    assetsGroups.forEach((assetGroup, index) => {
      if (assetGroup.platform !== LINKEDIN) return;
      setValue(`assetsGroups.[${index}].customAudiences`, []);
      setValue(`assetsGroups.[${index}].leadForm`, {});
    });
  };

  const onChangeAdFormat = useCallback(
    (newAdFormat) => {
      const assetsGroups = getValues("assetsGroups");
      assetsGroups.forEach((assetGroup, index) => {
        if (assetGroup.platform !== LINKEDIN) return;

        // Keep only first item of titles, headlines, descriptions, destinationUrls
        const currentTitles = assetGroup.titles || [];
        if (currentTitles.length > 0) {
          setValue(`assetsGroups.[${index}].titles`, [currentTitles[0]], {
            shouldValidate: true,
            shouldDirty: true,
          });
        }

        const currentHeadlines = assetGroup.headlines || [];
        if (currentHeadlines.length > 0) {
          setValue(`assetsGroups.[${index}].headlines`, [currentHeadlines[0]], {
            shouldValidate: true,
            shouldDirty: true,
          });
        }

        const currentDescriptions = assetGroup.descriptions || [];
        if (currentDescriptions.length > 0) {
          setValue(
            `assetsGroups.[${index}].descriptions`,
            [currentDescriptions[0]],
            { shouldValidate: true, shouldDirty: true }
          );
        }

        const currentDestinationUrls = assetGroup.destinationUrls || [];
        if (currentDestinationUrls.length > 0) {
          setValue(
            `assetsGroups.[${index}].destinationUrls`,
            [currentDestinationUrls[0]],
            { shouldValidate: true, shouldDirty: true }
          );
        }

        setValue(`assetsGroups.[${index}].isPostAd`, false);
        setValue(`assetsGroups.[${index}].mediaData`, []);
        setValue(`assetsGroups.[${index}].mediaUrls`, []);

        if (newAdFormat === "CAROUSEL") {
          setValue(`assetsGroups.[${index}].isPostAd`, false);
        }
      });
    },
    [getValues, setValue]
  );

  const onChangeObjective = (value) => {
    if (value === ADS_CAMPAIGN_OBJECTIVE_VIDEO_VIEWS_OPTION) {
      setValue(`${basicPath}.adFormat`, "SINGLE_VIDEO", {
        shouldValidate: true,
        shouldDirty: true,
      });
      onChangeAdFormat();
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      {!isCampaignPlatformCreated && <ReasoningContainer platform={platform} />}
      <Box
        sx={{
          width: "100%",
          minHeight: "100vh",
          display: "flex",
          flexDirection: containerFlexDirection,
          gap: 2,
        }}
      >
        <Box
          sx={{
            flex: 1,
            maxWidth: formMaxWidth,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 3,
            order: formOrder,
          }}
        >
          <Grid container spacing={2}>
            {/* CARD GENERAL CONFIGURATION */}
            <Grid item xs={12}>
              <CardContainer
                title={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {t(ADS_CAMPAIGN_AD_GROUP_BUDGET_INFORMATION_GENERAL)}
                    </Typography>
                  </Box>
                }
              >
                <Grid container spacing={3}>
                  <Grid item {...grid}>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      {t(AD_ACCOUNT_PLATFORMS_LABELS[platform])}
                    </Typography>
                    <SelectBusinessAccount
                      variant="filled"
                      disabled={isCampaignPlatformCreated || isDisabledFields}
                      control={control}
                      platform={platform}
                      name={`${basicPath}.adAccountID`}
                      onChangeAction={onChangeAdAccount}
                      validationChange={validationChangeAdAccount}
                      errors={errors}
                      fullWidth={true}
                      rules={{
                        required: {
                          value: true,
                          message: t(FIELD_REQUIRED),
                        },
                      }}
                    />
                  </Grid>

                  <Grid item {...grid}>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      {t(OBJECTIVE_PLACEHOLDER)}
                    </Typography>
                    <Select
                      name={`${basicPath}.objective`}
                      fullWidth
                      variant="filled"
                      doTranslate={true}
                      options={optionsAdsCampaignObjectivesLinkedin}
                      control={control}
                      errors={errors}
                      disabled={isDisabledFields}
                      actionsChange={onChangeObjective}
                      rules={{
                        required: {
                          value: true,
                          message: t(FIELD_REQUIRED),
                        },
                      }}
                    />
                  </Grid>

                  <Grid item {...grid}>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      {t(AD_FORMAT_PLACEHOLDER)}
                    </Typography>
                    <Select
                      name={`${basicPath}.adFormat`}
                      fullWidth
                      variant="filled"
                      doTranslate={true}
                      options={adFormatOptions}
                      control={control}
                      errors={errors}
                      disabled={
                        isCampaignPlatformCreated ||
                        isDisabledFields ||
                        objective === ADS_CAMPAIGN_OBJECTIVE_VIDEO_VIEWS_OPTION
                      }
                      actionsChange={(value) => {
                        onChangeAdFormat(value);
                      }}
                      rules={{
                        required: {
                          value: true,
                          message: t(FIELD_REQUIRED),
                        },
                      }}
                    />
                  </Grid>
                  <Grid item {...grid}>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      {t(CONVERSION_ACTIONS_PLACEHOLDER)}
                    </Typography>
                    <SelectConversionActionsLinkedin
                      name={`${basicPath}.conversionActions`}
                      control={control}
                      errors={errors}
                      integrationID={platformData?.integrationID}
                      adAccountID={platformData?.adAccountID}
                      objective={objective}
                      fullWidth
                      variant="filled"
                      disabled={isDisabledFields}
                    />
                  </Grid>
                </Grid>
              </CardContainer>
            </Grid>

            {/* CARD BUDGET */}
            <Grid item xs={12}>
              <CardContainer
                title={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {t(ADS_CAMPAIGN_AD_GROUP_BUDGET_INFORMATION_SUB_TITLE)}
                    </Typography>
                  </Box>
                }
              >
                <Grid container spacing={3}>
                  <Grid item {...grid}>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      {t(TYPE_BUDGET_PLACEHOLDER)}
                    </Typography>
                    <Select
                      name={`${basicPath}.typeBudget`}
                      fullWidth
                      variant="filled"
                      doTranslate={true}
                      options={optionsAdsCampaignTypeBudgets}
                      control={control}
                      errors={errors}
                      disabled={isCampaignPlatformCreated || isDisabledFields}
                      rules={{
                        required: {
                          value: true,
                          message: t(FIELD_REQUIRED),
                        },
                      }}
                    />
                  </Grid>
                  <Grid item {...grid}>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      {t(
                        typeBudget === ADS_CAMPAIGN_TYPE_BUDGET_DAILY_OPTION
                          ? DAILY_BUDGET_PLACEHOLDER
                          : LIFETIME_BUDGET_PLACEHOLDER,
                        {
                          currency: currency.toUpperCase(),
                        }
                      )}
                    </Typography>
                    <TextField
                      name={`${basicPath}.budget`}
                      fullWidth
                      variant="filled"
                      InputProps={{
                        inputComponent: linkedinCurrenciesWithDecimals.includes(
                          currency
                        )
                          ? NumberDecimalField
                          : NumberField,
                      }}
                      control={control}
                      errors={errors}
                      disabled={isDisabledFields}
                    />
                  </Grid>

                  <Grid item {...grid}>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      {t(START_DATE_PLACEHOLDER)}
                    </Typography>
                    <DateField
                      sx={{
                        backgroundColor: "fields.background",
                        boxShadow: BOX_SHADOW_FIELDS,
                        border: `1px solid ${INPUT_COLOR_BORDER}`,
                        borderRadius: BORDER_RADIUS,
                      }}
                      control={control}
                      name={`${basicPath}.startDate`}
                      minDate={startOfDay(new Date())}
                      actionsChange={(value) => {
                        setValue(
                          `${basicPath}.startDate`,
                          value ? startOfDay(value).toISOString() : null,
                          { shouldValidate: true, shouldDirty: true }
                        );
                      }}
                      fullWidth
                      variant="filled"
                      errors={errors}
                      disabled={isCampaignPlatformCreated || isDisabledFields}
                      showStartNowButton={true}
                    />
                  </Grid>

                  <Grid item {...grid}>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      {t(END_DATE_PLACEHOLDER)}
                    </Typography>
                    <DateField
                      sx={{
                        backgroundColor: "fields.background",
                        boxShadow: BOX_SHADOW_FIELDS,
                        border: `1px solid ${INPUT_COLOR_BORDER}`,
                        borderRadius: BORDER_RADIUS,
                      }}
                      control={control}
                      name={`${basicPath}.endDate`}
                      value={endDate || null}
                      minDate={addDays(
                        startDate ? new Date(startDate) : new Date(),
                        1
                      )}
                      actionsChange={(value) => {
                        setValue(
                          `${basicPath}.endDate`,
                          value ? startOfDay(value).toISOString() : null,
                          { shouldValidate: true, shouldDirty: true }
                        );
                      }}
                      clearable={true}
                      fullWidth
                      variant="filled"
                      errors={errors}
                      disabled={isDisabledFields}
                    />
                  </Grid>
                  {objective === ADS_CAMPAIGN_OBJECTIVE_LEADS_OPTION && (
                    <Grid item {...grid}>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        {t(TARGET_CPA_PLACEHOLDER)}
                      </Typography>
                      <TextField
                        name={`${basicPath}.targetCPA`}
                        fullWidth
                        variant="filled"
                        InputProps={{
                          inputComponent:
                            linkedinCurrenciesWithDecimals.includes(currency)
                              ? NumberDecimalField
                              : NumberField,
                        }}
                        control={control}
                        errors={errors}
                        disabled={isDisabledFields}
                        rules={{
                          required: {
                            value: true,
                            message: t(FIELD_REQUIRED),
                          },
                        }}
                      />
                    </Grid>
                  )}
                </Grid>
              </CardContainer>
            </Grid>

            {/* CARD SEGMENTATION */}
            <Grid item xs={12}>
              <CardContainer
                title={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {t(ADS_CAMPAIGN_SEGMENTATION_SUB_TITLE)}
                    </Typography>
                  </Box>
                }
              >
                {/* Demographics & Location */}
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  {/* Age & Gender */}
                  <Grid item xs={12} md={6}>
                    <CardContainer
                      title={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            flexWrap: "wrap",
                          }}
                        >
                          <Person
                            sx={{ color: "#FFFFFF", width: 24, height: 24 }}
                          />
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {t(DEMOGRAPHICS_BASIC_PLACEHOLDER)}
                          </Typography>
                          <ChipAIGeneratedAnySection
                            basePath={basicPath}
                            sections={AI_GENERATED_DEMOGRAPHICS_BASIC_SECTIONS}
                          />
                        </Box>
                      }
                    >
                      <Box sx={{ spaceY: 4 }}>
                        <Box>
                          <Typography variant="subtitle1" sx={{ mb: 2 }}>
                            {t(AGE_RANGE_PLACEHOLDER)}
                          </Typography>
                          <Controller
                            name={`${basicPath}.ageRange`}
                            control={control}
                            render={({ field }) => (
                              <Slider
                                {...field}
                                sx={{
                                  width: "calc(100% - 24px)",
                                  ml: 1,
                                  ".MuiSlider-track": {
                                    backgroundColor: "#000",
                                    height: 4,
                                  },
                                  ".MuiSlider-rail": {
                                    backgroundColor: "rgba(0,0,0,0.5)",
                                    height: 4,
                                  },
                                  ".MuiSlider-mark": {
                                    backgroundColor: "transparent",
                                  },
                                  ".MuiSlider-markLabel": {
                                    color: "rgba(0,0,0,0.5)",
                                  },
                                  ".MuiSlider-markLabelActive": {
                                    color: "#000",
                                  },
                                  ".MuiSlider-thumb": {
                                    border: "1px solid #e1e1e1",
                                    backgroundColor: "#fff",
                                  },
                                }}
                                valueLabelDisplay="auto"
                                getAriaValueText={textNumberCompactFormat}
                                step={1}
                                marks={marksAge}
                                min={18}
                                max={65}
                                valueLabelFormat={textNumberCompactFormat}
                                disabled={isDisabledFields}
                              />
                            )}
                          />
                        </Box>
                        <Divider sx={{ borderColor: INPUT_COLOR_BORDER }} />
                        <Box mt={4}>
                          <CheckBoxes
                            name={`${basicPath}.genders`}
                            disabled={isDisabledFields}
                            label={GENDERS_PLACEHOLDER}
                            options={[
                              {
                                key: "male",
                                label: MALES,
                              },
                              {
                                key: "female",
                                label: FEMALES,
                              },
                            ]}
                            control={control}
                            errors={errors}
                            rules={{
                              validate: () => {
                                if (!Object.values(genders).includes(true)) {
                                  return t(FIELD_REQUIRED);
                                }
                              },
                            }}
                          />
                        </Box>
                      </Box>
                    </CardContainer>
                  </Grid>

                  {/* Location */}
                  <Grid item xs={12} md={6}>
                    <CardContainer
                      title={
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <LocationOnOutlined
                            sx={{ color: "#FFFFFF", width: 24, height: 24 }}
                          />
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {t(LOCATIONS_PLACEHOLDER)}
                          </Typography>
                        </Box>
                      }
                      sxContainer={{
                        height: "100%",
                      }}
                    >
                      <SearchSegmentationsField
                        name={`${basicPath}.locations`}
                        mode="fetching"
                        platform={platform}
                        type="locations"
                        adAccountID={platformData?.adAccountID}
                        integrationID={platformData?.integrationID}
                        language={language}
                        fullWidth
                        variant="filled"
                        disabled={isDisabledFields}
                        control={control}
                        errors={errors}
                        rules={{
                          required: {
                            value: true,
                            message: t(FIELD_REQUIRED),
                          },
                        }}
                      />
                      <Box
                        sx={{
                          mt: 2,
                          mb: 1,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Typography variant="body1">
                          {t(LANGUAGES_PLACEHOLDER)}
                        </Typography>
                      </Box>
                      <SelectLanguagesLinkedinForm
                        name={`${basicPath}.languages`}
                        control={control}
                        disabled={isDisabledFields}
                        errors={errors}
                      />
                    </CardContainer>
                  </Grid>
                </Grid>

                {/* Audience Attributes */}
                <CardContainer
                  title={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      <People
                        sx={{ color: "#FFFFFF", width: 24, height: 24 }}
                      />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {t(AUDIENCE_ATTRIBUTES_PLACEHOLDER)}
                      </Typography>
                      <ChipAIGeneratedAnySection
                        basePath={basicPath}
                        sections={
                          AI_GENERATED_LINKEDIN_AUDIENCE_ATTRIBUTE_SECTIONS
                        }
                      />
                    </Box>
                  }
                >
                  <Grid container spacing={3}>
                    <CustomAudiencesContainerLinkedin
                      BASIC_PATH={basicPath}
                      name={`${basicPath}.customAudiences`}
                      control={control}
                      disabled={isDisabledFields}
                    />
                    {/* <Divider sx={{ my: 2, borderColor: INPUT_COLOR_BORDER }} />
                <CustomAudiencesContainerLinkedin
                  BASIC_PATH={basicPath}
                  name={`${basicPath}.excludedCustomAudiences`}
                  control={control}
                  disabled={isDisabledFields}
                  isExcludedAudiences={true}
                /> */}
                    <Grid item {...grid}>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        {t(INDUSTRIES_PLACEHOLDER)}
                      </Typography>
                      <SearchSegmentationsField
                        name={`${basicPath}.industries`}
                        type="industries"
                        mode="fetching"
                        platform={platform}
                        adAccountID={platformData?.adAccountID}
                        integrationID={platformData?.integrationID}
                        language={language}
                        placeholder={t(INDUSTRIES_SEARCH_PLACEHOLDER)}
                        fullWidth
                        variant="filled"
                        disabled={isDisabledFields}
                        control={control}
                        errors={errors}
                        rules={{
                          required: {
                            value: false,
                            message: t(FIELD_REQUIRED),
                          },
                        }}
                      />
                    </Grid>

                    <Grid item {...grid}>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        {t(JOB_FUNCTIONS_PLACEHOLDER)}
                      </Typography>
                      <SearchSegmentationsField
                        name={`${basicPath}.jobFunctions`}
                        type="jobFunctions"
                        mode="fetching"
                        platform={platform}
                        adAccountID={platformData?.adAccountID}
                        integrationID={platformData?.integrationID}
                        language={language}
                        placeholder={t(JOB_FUNCTIONS_SEARCH_PLACEHOLDER)}
                        fullWidth
                        variant="filled"
                        disabled={isDisabledFields}
                        control={control}
                        errors={errors}
                        fixedOptions={optionsJobFunctionsLinkedin}
                        rules={{
                          required: {
                            value: false,
                            message: t(FIELD_REQUIRED),
                          },
                        }}
                      />
                    </Grid>

                    <Grid item {...grid}>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        {t(SENIORITIES_PLACEHOLDER)}
                      </Typography>
                      <SearchSegmentationsField
                        name={`${basicPath}.seniorities`}
                        type="seniorities"
                        mode="fetching"
                        platform={platform}
                        adAccountID={platformData?.adAccountID}
                        integrationID={platformData?.integrationID}
                        language={language}
                        placeholder={t(SENIORITIES_SEARCH_PLACEHOLDER)}
                        fullWidth
                        variant="filled"
                        disabled={isDisabledFields}
                        control={control}
                        errors={errors}
                        fixedOptions={optionsSenioritiesLinkedin}
                        rules={{
                          required: {
                            value: false,
                            message: t(FIELD_REQUIRED),
                          },
                        }}
                      />
                    </Grid>

                    <Grid item {...grid}>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        {t(JOB_TITLES_PLACEHOLDER)}
                      </Typography>
                      <SearchSegmentationsField
                        name={`${basicPath}.jobTitles`}
                        type="jobTitles"
                        mode="fetching"
                        platform={platform}
                        adAccountID={platformData?.adAccountID}
                        integrationID={platformData?.integrationID}
                        language={language}
                        placeholder={t(JOB_TITLES_SEARCH_PLACEHOLDER)}
                        fullWidth
                        variant="filled"
                        disabled={isDisabledFields}
                        control={control}
                        errors={errors}
                        rules={{
                          required: {
                            value: false,
                            message: t(FIELD_REQUIRED),
                          },
                        }}
                      />
                    </Grid>

                    <Grid item {...grid}>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        {t(COMPANY_SIZES_PLACEHOLDER)}
                      </Typography>
                      <SearchSegmentationsField
                        name={`${basicPath}.companySizes`}
                        type="companySizes"
                        mode="fetching"
                        platform={platform}
                        adAccountID={platformData?.adAccountID}
                        integrationID={platformData?.integrationID}
                        language={language}
                        placeholder={t(COMPANY_SIZES_SEARCH_PLACEHOLDER)}
                        fullWidth
                        variant="filled"
                        disabled={isDisabledFields}
                        control={control}
                        errors={errors}
                        fixedOptions={optionsStaffCountRangesLinkedin}
                        rules={{
                          required: {
                            value: false,
                            message: t(FIELD_REQUIRED),
                          },
                        }}
                      />
                    </Grid>

                    <Grid item {...grid}>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        {t(COMPANIES_PLACEHOLDER)}
                      </Typography>
                      <SearchSegmentationsField
                        name={`${basicPath}.companies`}
                        type="companies"
                        mode="fetching"
                        platform={platform}
                        adAccountID={platformData?.adAccountID}
                        integrationID={platformData?.integrationID}
                        language={language}
                        placeholder={t(COMPANIES_SEARCH_PLACEHOLDER)}
                        fullWidth
                        variant="filled"
                        disabled={isDisabledFields}
                        control={control}
                        errors={errors}
                        rules={{
                          required: {
                            value: false,
                            message: t(FIELD_REQUIRED),
                          },
                        }}
                      />
                    </Grid>

                    <Grid item {...grid}>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        {t(DEGREES_PLACEHOLDER)}
                      </Typography>
                      <SearchSegmentationsField
                        name={`${basicPath}.degrees`}
                        type="degrees"
                        mode="fetching"
                        platform={platform}
                        adAccountID={platformData?.adAccountID}
                        integrationID={platformData?.integrationID}
                        language={language}
                        placeholder={t(DEGREES_SEARCH_PLACEHOLDER)}
                        fullWidth
                        variant="filled"
                        disabled={isDisabledFields}
                        control={control}
                        errors={errors}
                        rules={{
                          required: {
                            value: false,
                            message: t(FIELD_REQUIRED),
                          },
                        }}
                      />
                    </Grid>

                    <Grid item {...grid}>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        {t(FIELDS_OF_STUDY_PLACEHOLDER)}
                      </Typography>
                      <SearchSegmentationsField
                        name={`${basicPath}.fieldsOfStudy`}
                        type="fieldsOfStudy"
                        mode="fetching"
                        platform={platform}
                        adAccountID={platformData?.adAccountID}
                        integrationID={platformData?.integrationID}
                        language={language}
                        placeholder={t(FIELDS_OF_STUDY_SEARCH_PLACEHOLDER)}
                        fullWidth
                        variant="filled"
                        disabled={isDisabledFields}
                        control={control}
                        errors={errors}
                        rules={{
                          required: {
                            value: false,
                            message: t(FIELD_REQUIRED),
                          },
                        }}
                      />
                    </Grid>

                    <Grid item {...grid}>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        {t(SCHOOLS_PLACEHOLDER)}
                      </Typography>
                      <SearchSegmentationsField
                        name={`${basicPath}.schools`}
                        type="schools"
                        mode="fetching"
                        platform={platform}
                        adAccountID={platformData?.adAccountID}
                        integrationID={platformData?.integrationID}
                        language={language}
                        placeholder={t(SCHOOLS_SEARCH_PLACEHOLDER)}
                        fullWidth
                        variant="filled"
                        disabled={isDisabledFields}
                        control={control}
                        errors={errors}
                        rules={{
                          required: {
                            value: false,
                            message: t(FIELD_REQUIRED),
                          },
                        }}
                      />
                    </Grid>

                    <Grid item {...grid}>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        {t(SKILLS_PLACEHOLDER)}
                      </Typography>
                      <SearchSegmentationsField
                        name={`${basicPath}.skills`}
                        type="skills"
                        mode="fetching"
                        platform={platform}
                        adAccountID={platformData?.adAccountID}
                        integrationID={platformData?.integrationID}
                        language={language}
                        placeholder={t(SKILLS_SEARCH_PLACEHOLDER)}
                        fullWidth
                        variant="filled"
                        disabled={isDisabledFields}
                        control={control}
                        errors={errors}
                        rules={{
                          required: {
                            value: false,
                            message: t(FIELD_REQUIRED),
                          },
                        }}
                      />
                    </Grid>

                    <Grid item {...grid}>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        {t(INTERESTS_PLACEHOLDER)}
                      </Typography>
                      <SearchSegmentationsField
                        name={`${basicPath}.interests`}
                        type="interests"
                        mode="fetching"
                        platform={platform}
                        adAccountID={platformData?.adAccountID}
                        integrationID={platformData?.integrationID}
                        language={language}
                        placeholder={t(INTERESTS_SEARCH_PLACEHOLDER)}
                        fullWidth
                        variant="filled"
                        disabled={isDisabledFields}
                        control={control}
                        errors={errors}
                        rules={{
                          required: {
                            value: false,
                            message: t(FIELD_REQUIRED),
                          },
                        }}
                      />
                    </Grid>

                    <Grid item {...grid}>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        {t(MEMBER_BEHAVIORS_PLACEHOLDER)}
                      </Typography>
                      <SearchSegmentationsField
                        name={`${basicPath}.memberBehaviors`}
                        type="memberBehaviors"
                        mode="fetching"
                        platform={platform}
                        adAccountID={platformData?.adAccountID}
                        integrationID={platformData?.integrationID}
                        language={language}
                        placeholder={t(MEMBER_BEHAVIORS_SEARCH_PLACEHOLDER)}
                        fullWidth
                        variant="filled"
                        disabled={isDisabledFields}
                        control={control}
                        errors={errors}
                        fixedOptions={optionsMemberBehaviorsLinkedin}
                        rules={{
                          required: {
                            value: false,
                            message: t(FIELD_REQUIRED),
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </CardContainer>
              </CardContainer>
            </Grid>
          </Grid>
        </Box>
        <Box
          sx={{
            width: previewWidth,
            position: previewPosition,
            top: previewTop,
            alignSelf: "flex-start",
            height: "fit-content",
            overflow: "auto",
            order: previewOrder,
          }}
        >
          <AudienceEstimatesPreviewLinkedin BASIC_PATH={basicPath} />
        </Box>
      </Box>
    </Box>
  );
};

export default CampaignLinkedinForm;
